import createCache from '@emotion/cache';

let styleNonceObserverStarted = false;

const isValidNonce = (value) => {
  if (!value) return false;
  const trimmed = String(value).trim();
  if (!trimmed) return false;

  return !trimmed.includes('%') && !trimmed.includes('<') && !trimmed.includes('>');
};

const getAttributeNonce = (selector, attr = 'nonce') => {
  const element = document.querySelector(selector);
  if (!element) return undefined;

  return element.getAttribute(attr) || element[attr] || undefined;
};

const getMetaNonce = () => {
  const selectors = [
    'meta[property="csp-nonce"]',
    'meta[name="csp-nonce"]',
    'meta[name="nonce"]',
  ];

  return selectors
    .map((selector) => getAttributeNonce(selector, 'content'))
    .find((nonce) => isValidNonce(nonce));
};

const getGlobalNonce = () => {
  const {
    __CSP_NONCE__: cspNonce,
    __webpack_nonce__: webpackNonce,
  } = window;

  const nonce = cspNonce || webpackNonce;
  return isValidNonce(nonce) ? nonce : undefined;
};

const getDomNonce = () => {
  const selectors = ['script[nonce]', 'style[nonce]', '#root[nonce]', '#root[data-csp-nonce]'];

  return selectors
    .map((selector) => {
      const attribute = selector.includes('data-csp-nonce') ? 'data-csp-nonce' : 'nonce';
      return getAttributeNonce(selector, attribute);
    })
    .find((nonce) => isValidNonce(nonce));
};

const applyNonceToStyleTags = (nonce) => {
  if (!isValidNonce(nonce) || typeof document === 'undefined') {
    return;
  }

  const setNonce = (element) => {
    if (element?.tagName === 'STYLE' && !element.getAttribute('nonce')) {
      element.setAttribute('nonce', nonce);
    }
  };

  document.querySelectorAll('style').forEach(setNonce);

  if (styleNonceObserverStarted || typeof MutationObserver === 'undefined') {
    return;
  }

  const { head } = document;
  if (!head) {
    return;
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) {
          return;
        }

        setNonce(node);
        if (typeof node.querySelectorAll === 'function') {
          node.querySelectorAll('style').forEach(setNonce);
        }
      });
    });
  });

  observer.observe(head, { childList: true, subtree: true });
  styleNonceObserverStarted = true;
};

export const resolveCspNonce = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return undefined;
  }

  const nonce = getGlobalNonce() || getMetaNonce() || getDomNonce();
  applyNonceToStyleTags(nonce);

  return nonce;
};

export const createEmotionCache = () =>
  createCache({
    key: 'css',
    nonce: resolveCspNonce(),
    prepend: true,
  });

export default createEmotionCache();
